import PropTypes from 'prop-types';
import React from 'react';
import { getContent } from '../../../containers/ReturnPolicy/ReturnPolicyModal/ReturnPolicy.helper';
import { getTenant } from '../../../utils/tenantHelper';
import { cssClasses, returnPolicyContentStyles } from './ReturnPolicyContent.styles';
import {
  generateListItemKey,
  getListClasses,
  getParagraphClasses,
  getParagraphWrapperClasses,
  getShouldShowDivider,
} from './ReturnPolicyContent.utils';

/**
 * ReturnPolicyContent Component
 *
 * Renders return policy content sections with tenant-specific styling and layout.
 * Handles different content types: paragraphs, lists, icons, and dividers.
 */
const ReturnPolicyContent = ({ regionCode, labels, store }) => {
  const currentTenant = getTenant(regionCode);
  const isChileanTenant = currentTenant === 'cl';
  const isPeruvianTenant = currentTenant === 'pe';

  // Generate content sections using the helper function
  const contentSections = getContent(regionCode, labels, store);

  /**
   * Renders a paragraph with tenant-specific styling
   */
  const renderParagraph = (paragraphContent, paragraphType, sectionIndex, hasIcon = false) => {
    if (!paragraphContent) return null;

    const paragraphClasses = getParagraphClasses(paragraphType, sectionIndex, isChileanTenant, isPeruvianTenant);
    const wrapperClasses = getParagraphWrapperClasses(isChileanTenant);

    return (
      <div className={wrapperClasses}>
        {hasIcon && <div className={cssClasses.icon.base} />}
        <p className={paragraphClasses} dangerouslySetInnerHTML={{ __html: paragraphContent }} />
      </div>
    );
  };

  /**
   * Renders a list of items from string content
   */
  const renderList = (listContent, sectionTitle, hasPreviousParagraphs) => {
    if (!listContent) return null;

    const listClasses = getListClasses(isChileanTenant, hasPreviousParagraphs);
    const listItems = listContent.split('*');

    return (
      <ul
        className={listClasses}
        style={{
          margin: '1rem 0',
          paddingLeft: '1.25rem',
          listStyleType: 'disc',
        }}
      >
        {listItems.map((item) => {
          const sanitizedItem = item.trim();
          if (!sanitizedItem) return null;

          const itemKey = generateListItemKey(sectionTitle, sanitizedItem);

          return (
            <li
              key={itemKey}
              className={cssClasses.list.item}
              style={{
                marginBottom: '0.5rem',
                textAlign: 'justify',
                fontSize: '14px',
                lineHeight: '1.5',
                color: '#333333',
              }}
              dangerouslySetInnerHTML={{ __html: sanitizedItem }}
            />
          );
        })}
      </ul>
    );
  };

  /**
   * Renders a divider line based on tenant and position
   */
  const renderDivider = (sectionIndex, totalSections, hasSecondParagraph) => {
    const shouldShowDivider = getShouldShowDivider(isChileanTenant, sectionIndex, totalSections, hasSecondParagraph);

    if (!shouldShowDivider) return null;

    return <div className={cssClasses.divider.base} />;
  };

  /**
   * Renders a complete content section
   */
  const renderContentSection = (contentSection, sectionIndex) => {
    const { title, para1, para2, para2Icon, para3, para3Icon, list } = contentSection;
    const sectionKey = title || `section-${sectionIndex}`;
    const hasPreviousParagraphs = !!(para1 || para2);

    return (
      <React.Fragment key={sectionKey}>
        <div className={cssClasses.section}>
          {/* First paragraph */}
          {renderParagraph(para1, 'primary', sectionIndex)}

          {/* Divider after first paragraph (Chilean tenant only) */}
          {renderDivider(sectionIndex, contentSections.length, !!para2)}

          {/* Second paragraph with optional icon */}
          {renderParagraph(para2, 'secondary', sectionIndex, !!para2Icon)}

          {/* List content */}
          {renderList(list, title, hasPreviousParagraphs)}

          {/* Third paragraph with optional icon */}
          {renderParagraph(para3, 'tertiary', sectionIndex, !!para3Icon)}

          {/* Section divider (non-Chilean tenants) */}
          {renderDivider(sectionIndex, contentSections.length, false)}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div className={cssClasses.container}>
      {contentSections.map((contentSection, sectionIndex) => renderContentSection(contentSection, sectionIndex))}
      <style jsx>{returnPolicyContentStyles}</style>
    </div>
  );
};

ReturnPolicyContent.propTypes = {
  /** Region code to determine tenant-specific styling and content */
  regionCode: PropTypes.string.isRequired,
  /** Labels object containing localized text */
  labels: PropTypes.object.isRequired,
  /** Store identifier for store-specific content */
  store: PropTypes.string.isRequired,
};

export { ReturnPolicyContent };
export default ReturnPolicyContent;
